import { create } from 'zustand';
import { getTenantId } from './authStore';

const ONTOLOGY_API = import.meta.env.VITE_ONTOLOGY_SERVICE_URL || 'http://localhost:8004';

export interface EffectConfig {
  kind: 'run_logic_function' | 'run_pipeline' | 'ontology_write' | 'webhook' | 'connector_upload';
  function_id?: string;
  inputs_template?: Record<string, unknown>;
  pipeline_id?: string;
  url?: string;
  secret?: string;
  payload_template?: Record<string, unknown>;
  // connector_upload — all values allow $inputs/$fields tokens
  connector_id?: string;
  source?: string;
  target_folder?: string;
  filename_template?: string;
}

export interface DecisionOptionField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'file';
  label?: string;
  required?: boolean;
  default?: unknown;
  options?: string[];
  // file — the decided value is a Document id from POST /documents/upload
  accept?: string;
  max_mb?: number;
}

export interface DecisionOption {
  id: string;
  label: string;
  style?: 'primary' | 'danger' | 'default';
  description?: string;
  terminal_status?: 'completed' | 'rejected';
  fields?: DecisionOptionField[];
  effects?: EffectConfig[];
}

export interface EffectResult {
  kind: string;
  status: 'ok' | 'error';
  error?: string;
  detail?: {
    run_id?: string;
    run_status?: string;
    function_id?: string;
    pipeline_id?: string;
    url?: string;
    status_code?: number;
    record_id?: string;
    object_type_id?: string;
    op?: string;
  };
}

export interface DecisionResult {
  action?: string;
  decided_by?: string;
  option_id?: string;
  option_label?: string;
  fields?: Record<string, unknown>;
  effects?: EffectResult[];
  [key: string]: unknown;
}

/** Supporting material an agent attaches to a proposal — Document records
 *  (email attachments, uploads), the record the case hangs off, and links. */
export interface ActionEvidence {
  document_ids?: string[];
  record_ref?: { object_type: string; record_id: string } | null;
  links?: { label: string; url: string }[];
}

export interface ActionExecution {
  id: string;
  tenant_id: string;
  action_name: string;
  inputs: Record<string, unknown>;
  status: 'pending_confirmation' | 'completed' | 'rejected';
  result?: DecisionResult | null;
  error?: string;
  executed_by?: string;
  confirmed_by?: string;
  rejected_by?: string;
  rejection_reason?: string;
  source?: string;
  source_id?: string;
  reasoning?: string;
  evidence?: ActionEvidence | null;
  selected_option_ids?: string[];
  created_at?: string;
  updated_at?: string;
}

interface HumanActionsStore {
  pending: ActionExecution[];
  history: ActionExecution[];
  loading: boolean;
  pendingCount: number;

  fetchPending: () => Promise<void>;
  fetchHistory: () => Promise<void>;
  confirm: (executionId: string, confirmedBy?: string, note?: string) => Promise<void>;
  reject: (executionId: string, rejectedBy: string, reason: string) => Promise<void>;
  decide: (executionId: string, optionId: string, fields: Record<string, unknown>, decidedBy?: string, note?: string) => Promise<ActionExecution>;
  bulkConfirm: (ids: string[], confirmedBy?: string) => Promise<{ succeeded: number; failed: number }>;
  bulkReject: (ids: string[], rejectedBy: string, reason: string) => Promise<{ succeeded: number; failed: number }>;
  /** Drop all tenant-scoped state on sign-out / user switch (see store/sessionReset). */
  reset: () => void;
}

export const useHumanActionsStore = create<HumanActionsStore>((set, get) => ({
  pending: [],
  history: [],
  loading: false,
  pendingCount: 0,

  fetchPending: async () => {
    set({ loading: true });
    try {
      const r = await fetch(`${ONTOLOGY_API}/actions/executions/pending`, {
        headers: { 'x-tenant-id': getTenantId() },
      });
      const data = await r.json();
      const pending = Array.isArray(data) ? data : [];
      set({ pending, pendingCount: pending.length });
    } finally {
      set({ loading: false });
    }
  },

  fetchHistory: async () => {
    // Fetch recent completed + rejected across all actions by loading
    // per-action executions. For now we load from the pending list endpoint
    // by passing status filter (the API supports ?status= per action).
    // Since actions/executions/pending only returns pending, we use a broader query.
    // We'll fetch up to 100 executions via the global executions endpoint.
    try {
      const r = await fetch(`${ONTOLOGY_API}/actions/executions?limit=100`, {
        headers: { 'x-tenant-id': getTenantId() },
      });
      if (r.ok) {
        const data = await r.json();
        set({ history: Array.isArray(data) ? data.filter((e: ActionExecution) => e.status !== 'pending_confirmation') : [] });
      }
    } catch {
      // history endpoint may not exist — silently ignore
    }
  },

  confirm: async (executionId, confirmedBy = 'admin', note) => {
    const r = await fetch(`${ONTOLOGY_API}/actions/executions/${executionId}/confirm`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmed_by: confirmedBy, note }),
    });
    if (r.ok) {
      const updated = await r.json();
      set((s) => ({
        pending: s.pending.filter((e) => e.id !== executionId),
        pendingCount: Math.max(0, s.pendingCount - 1),
        history: [updated, ...s.history],
      }));
    }
  },

  reject: async (executionId, rejectedBy, reason) => {
    const r = await fetch(`${ONTOLOGY_API}/actions/executions/${executionId}/reject`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ rejected_by: rejectedBy, reason }),
    });
    if (r.ok) {
      const updated = await r.json();
      set((s) => ({
        pending: s.pending.filter((e) => e.id !== executionId),
        pendingCount: Math.max(0, s.pendingCount - 1),
        history: [updated, ...s.history],
      }));
    }
  },

  decide: async (executionId, optionId, fields, decidedBy = 'admin', note) => {
    const r = await fetch(`${ONTOLOGY_API}/actions/executions/${executionId}/decide`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ option_id: optionId, fields, decided_by: decidedBy, note }),
    });
    if (!r.ok) {
      let detail = `HTTP ${r.status}`;
      try { detail = (await r.json()).detail || detail; } catch { /* keep status */ }
      throw new Error(detail);
    }
    const updated = await r.json();
    set((s) => ({
      pending: s.pending.filter((e) => e.id !== executionId),
      pendingCount: Math.max(0, s.pendingCount - 1),
      history: [updated, ...s.history],
    }));
    return updated as ActionExecution;
  },

  bulkConfirm: async (ids, confirmedBy = 'admin') => {
    const results = await Promise.allSettled(
      ids.map((id) =>
        fetch(`${ONTOLOGY_API}/actions/executions/${id}/confirm`, {
          method: 'POST',
          headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ confirmed_by: confirmedBy }),
        }).then((r) => r.ok ? r.json() : Promise.reject(r.status))
      )
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    const updated = results
      .filter((r): r is PromiseFulfilledResult<ActionExecution> => r.status === 'fulfilled')
      .map((r) => r.value);
    set((s) => ({
      pending: s.pending.filter((e) => !ids.includes(e.id)),
      pendingCount: Math.max(0, s.pendingCount - succeeded),
      history: [...updated, ...s.history],
    }));
    return { succeeded, failed };
  },

  bulkReject: async (ids, rejectedBy, reason) => {
    const results = await Promise.allSettled(
      ids.map((id) =>
        fetch(`${ONTOLOGY_API}/actions/executions/${id}/reject`, {
          method: 'POST',
          headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ rejected_by: rejectedBy, reason }),
        }).then((r) => r.ok ? r.json() : Promise.reject(r.status))
      )
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    const updated = results
      .filter((r): r is PromiseFulfilledResult<ActionExecution> => r.status === 'fulfilled')
      .map((r) => r.value);
    set((s) => ({
      pending: s.pending.filter((e) => !ids.includes(e.id)),
      pendingCount: Math.max(0, s.pendingCount - succeeded),
      history: [...updated, ...s.history],
    }));
    return { succeeded, failed };
  },

  reset: () => set({ pending: [], history: [], pendingCount: 0, loading: false }),
}));
